import { experience } from "@/data/portfolioData";
import { useReveal } from "@/hooks/useReveal";

function WorkLocked({ games }) {
  const {
    workQuestion,
    workError,
    workFailCount,
    workInputRef,
    checkWork,
    bypassWork,
    fightOpen,
    fightNotOpen,
    openFight,
    fightRound,
    fightWon,
    fightMessage,
    fightPrompt,
    fightOptions,
    playerHearts,
    bossHearts,
    claimVictory,
    fightResolve,
  } = games;

  return (
    <div className="mx-auto max-w-[640px] rounded-[20px] border-3 border-ink bg-ink p-10 shadow-neo-lg shadow-periwinkle">
      <div className="mb-[18px] flex gap-2">
        <span className="inline-block h-3 w-3 rounded-full bg-pink" />
        <span className="inline-block h-3 w-3 rounded-full bg-yellow" />
        <span className="inline-block h-3 w-3 rounded-full bg-green" />
      </div>

      {!fightOpen && (
        <>
          <p className="mb-1.5 font-mono text-[15px] text-green">$ section locked 🔒</p>
          <p className="mb-4 text-base leading-relaxed text-cream">{workQuestion}</p>

          {workFailCount === 1 && <p className="mb-2.5 text-[34px]">😳</p>}
          {workFailCount >= 2 && (
            <p className="mb-2.5 inline-block animate-shakeMeme font-display text-[32px] text-pink">
              FAAAAAH 😭
            </p>
          )}

          <div className="flex flex-wrap gap-2.5">
            <input
              ref={workInputRef}
              type="text"
              placeholder="type your answer..."
              onKeyDown={(e) => e.key === "Enter" && checkWork()}
              className="min-w-[200px] flex-1 rounded-[10px] border-2 border-green bg-[#1F1F1F] px-3.5 py-3 font-mono text-[15px] text-green outline-none"
            />
            <button
              type="button"
              onClick={checkWork}
              className="cursor-pointer rounded-[10px] border-2 border-green bg-green px-[22px] py-3 font-display text-sm text-ink transition-transform hover:-translate-y-0.5"
            >
              Run ▶
            </button>
          </div>

          {workError && (
            <p className="mt-2.5 font-mono text-[13px] text-pink">
              bash: command not found. try again.
            </p>
          )}
        </>
      )}

      {fightOpen && (
        <>
          <p className="mb-3.5 font-mono text-[15px] text-green">
            ⚔️ BUG BOSS BATTLE · Round {fightRound}/2
          </p>
          <div className="mb-4 flex gap-6">
            <p className="m-0 text-sm text-cream">You: {playerHearts}</p>
            <p className="m-0 text-sm text-pink">Bug boss: {bossHearts}</p>
          </div>

          {fightWon ? (
            <>
              <p className="mb-[18px] font-display text-[22px] text-green">KO! 🏆 You beat the bug.</p>
              <button
                type="button"
                onClick={claimVictory}
                className="cursor-pointer rounded-[10px] border-2 border-green bg-green px-[22px] py-3 font-display text-sm text-ink"
              >
                Claim the section →
              </button>
            </>
          ) : (
            <>
              <p className="mb-1.5 text-base leading-relaxed text-cream">{fightPrompt}</p>
              {fightMessage && (
                <p className="mb-3.5 font-mono text-[13px] text-yellow">{fightMessage}</p>
              )}
              <div className="mt-2.5 flex flex-wrap gap-2.5">
                {fightOptions.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => fightResolve(opt.correct)}
                    className="cursor-pointer rounded-[10px] border-2 border-cream bg-cream px-[18px] py-3 text-sm font-bold text-ink transition-transform hover:-translate-y-0.5"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        {fightNotOpen && (
          <button
            type="button"
            onClick={openFight}
            className="cursor-pointer rounded-[10px] border-2 border-dashed border-pink bg-transparent px-4 py-2.5 text-[13px] font-bold text-pink transition-all hover:scale-[1.03] hover:bg-pink/10"
          >
            You wanna fight for it? 🥊
          </button>
        )}
        <button
          type="button"
          onClick={bypassWork}
          className="cursor-pointer rounded-[10px] border-2 border-dashed border-yellow bg-transparent px-4 py-2.5 text-[13px] font-bold text-yellow transition-all hover:scale-[1.03] hover:bg-yellow/10"
        >
          I am not fun 🙄
        </button>
      </div>
    </div>
  );
}

function WorkTimeline() {
  const revealRef = useReveal();

  return (
    <div ref={revealRef} className="mx-auto flex max-w-[820px] flex-col gap-0">
      {experience.map((job, index) => (
        <div key={job.company} className="work-grid grid grid-cols-[170px_40px_1fr] gap-0 max-md:grid-cols-1">
          <div className="pt-1.5 font-display text-[15px] text-ink max-md:mb-1">{job.period}</div>
          <div className="flex flex-col items-center max-md:hidden">
            <div
              className={`h-5 w-5 rounded-full border-3 border-ink ${job.dotColor} animate-pulseDot`}
              style={index > 0 ? { animationDelay: `${index * 0.3}s` } : undefined}
            />
            {index < experience.length - 1 && <div className="w-[3px] flex-1 bg-ink" />}
          </div>
          <div className={index < experience.length - 1 ? "pb-10" : "pb-0"}>
            <h3 className="mb-1.5 font-display text-xl text-ink">
              {job.title} · {job.company}
            </h3>
            <p className="m-0 text-[15px] leading-normal text-muted2">{job.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Work({ games }) {
  return (
    <section id="work" className="relative z-[2] px-6 pb-[70px] pt-8 md:px-14">
      <p className="mb-1.5 text-center text-base font-bold text-teal">Where I&apos;ve built things</p>
      <h2 className="mb-12 text-center font-display text-[44px] text-ink">Work experience</h2>

      {!games.workUnlocked ? <WorkLocked games={games} /> : <WorkTimeline />}
    </section>
  );
}
